/**
 * Shared layout bits for path settings panels.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import type {
  VoluumPathRealtimeRoutingState,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { Switch } from '@/components/ui/form/controls/Switch';

export const PathSettingsDivider: FC = () => (
  <div className="border-t border-border/80" />
);

export const PathSettingsSectionTitle: FC<{
  children: string;
  hint?: string;
  /** Smaller heading (split-panel compact layout). */
  dense?: boolean;
}> = ({ children, hint, dense = false }) => (
  <div className="flex items-center gap-1.5">
    <h4
      className={
        dense
          ? 'text-xs font-semibold text-foreground'
          : 'text-sm font-semibold text-foreground'
      }
    >
      {children}
    </h4>
    {hint ? (
      <Tooltip content={hint} side="top">
        <HelpCircle
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          aria-label="Help"
        />
      </Tooltip>
    ) : null}
  </div>
);

export const PathSettingsFieldLabel: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
    {children}
  </span>
);

export const PathSettingsMoreFields: FC<{
  path: VoluumPathUpdate;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
  compact?: boolean;
}> = ({ path, onPatch, compact = false }) => (
  <div
    className={
      compact
        ? 'space-y-2 rounded-md border border-border/50 bg-muted/15 p-2'
        : 'space-y-3 rounded-md border border-border/50 bg-muted/15 p-3'
    }
  >
    <p
      className={
        compact
          ? 'text-[11px] font-semibold text-foreground'
          : 'text-xs font-semibold text-foreground'
      }
    >
      Additional options
    </p>
    <div className="flex min-w-0 items-center gap-2">
      <Switch
        checked={path.listicle}
        onCheckedChange={(v) =>
          onPatch((p) => {
            p.listicle = v;
          })
        }
        aria-label="Listicle path (multi-lander)"
      />
      <span
        className={
          compact
            ? 'text-xs font-medium text-foreground'
            : 'text-sm font-medium text-foreground'
        }
      >
        Listicle path
      </span>
      <Tooltip
        content="Voluum “listicle” paths use multiple landers (including sublanders) in a list-style funnel."
        side="top"
      >
        <HelpCircle
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          aria-label="About listicle paths"
        />
      </Tooltip>
    </div>
    <label
      className={
        compact
          ? 'flex flex-wrap items-center gap-2 text-xs'
          : 'flex flex-wrap items-center gap-2 text-sm'
      }
    >
      <span className="text-muted-foreground">Realtime routing API</span>
      <select
        value={path.realtimeRoutingApiState}
        onChange={(e) =>
          onPatch((p) => {
            p.realtimeRoutingApiState = e.target
              .value as VoluumPathRealtimeRoutingState;
          })
        }
        className="h-8 rounded-md border border-input bg-background px-2 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label="Realtime routing API state for this path"
      >
        <option value="DISABLED">Off</option>
        <option value="ENABLED">On</option>
      </select>
    </label>
  </div>
);
